<template>
  <div class="p-drawer">
    <!-- アイコン部 -->
    <div class="p-drawer__icons">
      <a
        :class="{ 'p-drawer__icons__expand': true, 'is-disable': isSidebarWidthMax }"
        @click="expand"
      >
        <v-icon
          icon="mdi-play-circle"
          size="large"
        />
      </a>
      <a
        :class="{ 'p-drawer__icons__shrink': true, 'is-disable': isSidebarWidthMin }"
        @click="shrink"
      >
        <v-icon
          icon="mdi-play-circle"
          size="large"
        />
      </a>
    </div>

    <!-- サイドバー本体部 -->
    <v-navigation-drawer
      class="p-drawer__main"
      :width="sidebarWidth"
      location="right"
      permanent
    >
      <!-- 最小化時の表示 -->
      <a
        v-if="isSidebarWidthMin"
        style="display:block; height: 100%; width: 100%;"
        @click="expand"
      >
        <v-icon class="pt-4 pl-1">search</v-icon>
      </a>
      <div v-show="!isSidebarWidthMin">
        <!-- タブ一覧 -->
        <!-- タブ一覧 -->
        <v-tabs
          v-model="selectTabId"
          :height="40"
          bg-color="grey-lighten-3"
        >
          <v-tab
            :key="SIDEBAR_TAB_IDS.SIMILAR_SEARCH"
            class="ma-0"
            :height="40"
            :value="SIDEBAR_TAB_IDS.SIMILAR_SEARCH"
          >
            <v-icon>mdi-magnify</v-icon>
            <span class="font-weight-bold">過去事例検索</span>
          </v-tab>
          <v-tab
            :key="SIDEBAR_TAB_IDS.AI_CORRECTION"
            class="ma-0"
            :height="40"
            :value="SIDEBAR_TAB_IDS.AI_CORRECTION"
          >
            <RobotIcon class="svg-correctionBlue-fill" />
            <span class="font-weight-bold">AI添削</span>
          </v-tab>
          <v-tab
            :key="SIDEBAR_TAB_IDS.JOURNAL"
            class="ma-0"
            :height="40"
            :value="SIDEBAR_TAB_IDS.JOURNAL"
          >
            <v-icon class="mr-1">
              mdi-book-open-page-variant
            </v-icon>
            <span class="font-weight-bold">ジャーナル検索</span>
          </v-tab>
        </v-tabs>

        <!-- タブごとの表示切り替え -->
        <div
          class="bg-confirmLightBlue"
          style="min-height: calc(100vh - 40px);"
        >
          <AnytimeConsultationSimilarSearchPanel
            v-show="selectTabId === SIDEBAR_TAB_IDS.SIMILAR_SEARCH"
            ref="anytimeConsultationSimilarSearchPanelRef"
          /> <!-- タブ「過去事例検索」 -->
          <AnytimeConsultationCorrectionTab
            v-show="selectTabId === SIDEBAR_TAB_IDS.AI_CORRECTION"
            ref="anytimeConsultationCorrectionTab"
          /> <!-- タブ「回答文添削」 -->
          <journal-tab 
            v-show="selectTabId === SIDEBAR_TAB_IDS.JOURNAL"
            ref="journalTabRef"
            type="anytimeConsultation"
          /> <!-- タブ「ジャーナル検索」 -->
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";

// 検索
import AnytimeConsultationSimilarSearchPanel from "@/vue/widgets/anytime-consultation-similar-search/ui/SearchTabPanel.vue";

// 添削
import AnytimeConsultationCorrectionTab from "@/vue/widgets/anytime-consultation-correction/ui/AnytimeConsultationCorrectionTab.vue";
import RobotIcon from "@/vue/components/images/robotIcon.vue";

// ジャーナル検索
import JournalTab from "@/vue/components/shared/sidebar/Journal/journalTab.vue";

import { SIDEBAR_TAB_IDS } from "@/vue/features/anytime-consultation-sidebar/model/sidebarTabIds";

const store = useStore();
const route = useRoute();

// JournalTabコンポーネントの参照
const journalTabRef = ref(null);
// AnytimeConsultationSimilarSearchPanelコンポーネントの参照
const anytimeConsultationSimilarSearchPanelRef = ref(null);

const sidebarTabId = computed(() => store.getters.sidebarTabId);
const sidebarWidthRank = computed(() => store.getters.sidebarWidthRank);
const sidebarWidth = computed(() => store.getters.sidebarWidth);
const isSidebarWidthMin = computed(() => store.getters.isSidebarWidthMin);
const isSidebarWidthMax = computed(() => store.getters.isSidebarWidthMax);
const anytimeConsultation = computed(() => store.getters.anytimeConsultation);

const selectTabId = computed({
  get: () => sidebarTabId.value,
  set: (val) => store.dispatch("selectSidebarTabId", val),
});

const expand = () => {
  if (!isSidebarWidthMax.value) {
    store.dispatch("updateSidebarWidthRank", sidebarWidthRank.value + 1);
  }
};

const shrink = () => {
  if (!isSidebarWidthMin.value) {
    store.dispatch("updateSidebarWidthRank", sidebarWidthRank.value - 1);
  }
};

// --- ▼ AnytimeConsultationのFetchが完了/失敗したタイミングで、サイドバーの各タブの初期化をトリガーする ---
const initializeTabs = () => {
  store.dispatch("updateSidebarWidthRank", 2); // サイドバーの幅を初期化
  if (!!anytimeConsultation.value && anytimeConsultation.value.id == route.params.id) {
    // AnytimeConsultationが存在し、現在のルートのIDと一致する場合、タブの初期検索を実行
    if (anytimeConsultationSimilarSearchPanelRef.value) anytimeConsultationSimilarSearchPanelRef.value.start();
    if (journalTabRef.value) journalTabRef.value.start();
  } else {
    // AnytimeConsultationが存在しない、または現在のルートのIDと一致しない場合、タブの中身をクリア
    if (anytimeConsultationSimilarSearchPanelRef.value) anytimeConsultationSimilarSearchPanelRef.value.clear();
    if (journalTabRef.value) journalTabRef.value.clear();
  }
};

// コンポーネントのマウント時と、anytimeConsultationの変更時にサイドバーの各タブの初期化を実行
onMounted(() => {
  initializeTabs();
});
watch(
  () => anytimeConsultation.value,
  () => {
    initializeTabs();
  }
);
// --- ▲ AnytimeConsultationのFetchが完了/失敗したタイミングで、サイドバーの各タブの初期化をトリガーする ---
</script>

<style lang="scss" scoped>
.p-drawer {
  &__icons {
    position: absolute;
    z-index: 2000;
    /* v-navigation-drawerになぜか動的にz-indexが指定される */
    margin-left: -12px;
    display: flex;
    flex-direction: column;
    margin-top: 50px;

    &__expand {
      transform: rotate(180deg);
      height: 25px;

      &.is-disable {
        opacity: 0;
      }
    }

    &__shrink {
      height: 25px;
      margin-top: 8px;

      &.is-disable {
        opacity: 0;
      }
    }
  }

  &__main {
    border-left: 1px solid #C9C5C5;
    height: 100vh !important;
    overflow: auto;
  }

  :deep(.v-navigation-drawer) {
    /* absoluteがつくとflexでうまく配置されない */
    position: initial !important;
  }
}

:deep(.custom-tabs .v-tab) {
  background-color: #F5F5F5;
}
</style>